// Tipos compartidos entre backend y frontend.
// Mantener en sync con apps/api/src/types.ts y apps/web/src/lib/api.ts.

export type Role = "super_admin" | "admin";
export type GameType = "ruleta" | "raspadita" | "memoria";
export type RaspaditaMecanica = "panel" | "grid" | "elije";
export type RaspaditaGridDinamica = "encontrar" | "intentos" | "todas";
// SoundStyle es ahora un STRING libre (slug). Hasta migration 029 era
// un enum de 5 valores hardcoded. Ahora cada slug viene de un row
// en la tabla sound_styles, gestionada por super_admin. El frontend
// valida runtime contra la lista cargada del endpoint
// /api/admin/sound-styles. Slugs típicos: 'classic', 'casino',
// 'festivo', 'silent', 'mundial' (los 5 seed iniciales), + cualquiera
// que el super_admin agregue.
export type SoundStyle = string;

// Row pública de sound_styles para el selector del admin de sucursal.
// Solo los campos legibles — sort_order/created_at no se exponen.
export type SoundStylePublic = {
  id: number;
  slug: string;
  label: string;
  description: string | null;
  activo: boolean;
  sort_order: number;
};

// ──────────────────────────────────────────────
// Eventos sonoros por juego (refactor sonidos 2026-06-02)
// ──────────────────────────────────────────────
// Cada juego tiene su set de eventos sonoros. El super_admin sube un
// archivo por (estilo, juego, evento) — slot key convencional:
//   `${style_slug}-${juego}-${evento}` (ej: classic-ruleta-tick)
//
// Cascada de lookup en audio.ts:
//   1. slot exacto: `${style}-${juego}-${evento}` (subido por super_admin
//      para este estilo/juego/evento específico).
//   2. slot legacy (compat con uploads previos al refactor):
//      `tick`, `spin-start`, `blink`, `win-${style}`, `consolation`,
//      `retry`. Solo para los eventos que existían antes.
//   3. sintético generado por Web Audio API (definido en audio.ts).
//
// Decisión §20-friendly: agregar un evento nuevo aquí + actualizar
// audio.ts + audio puede tener un nuevo sintético. La UI admin se
// re-genera sola desde esta lista — no hay que tocar JSX.
export type EventoRuleta = "spin-start" | "tick" | "blink" | "win" | "consolation";
export type EventoRaspadita = "scratch" | "reveal" | "win" | "consolation";
export type EventoMemoria = "flip" | "match" | "mismatch" | "win" | "lose";

// Tipo de sintético fallback — discriminante para audio.ts.
export type SyntheticFallback =
  | "click"
  | "spin-start"
  | "blink"
  | "fanfarria"
  | "consolation"
  | "scratch"
  | "reveal"
  | "flip"
  | "match"
  | "mismatch"
  | "lose";

export type GameSoundEventSpec = {
  juego: GameType;
  evento: string;
  label: string;
  descripcion: string;
  // Slot legacy correspondiente del modelo viejo (pre-refactor 2026-06-02).
  // null si el evento es nuevo y no tenía equivalente.
  legacySlot: string | null;
  // Hint al usar sound_style: cuando el slot legacy depende del estilo
  // (ej. win-${style}, consolation por estilo), poner true.
  legacyDependsOnStyle: boolean;
  synthetic: SyntheticFallback;
};

export const GAME_SOUND_EVENTS: ReadonlyArray<GameSoundEventSpec> = [
  // ── Ruleta ──
  {
    juego: "ruleta",
    evento: "spin-start",
    label: "Inicio del giro",
    descripcion: "Suena al apretar GIRAR. Whoosh corto tipo lanzamiento.",
    legacySlot: "spin-start",
    legacyDependsOnStyle: false,
    synthetic: "spin-start",
  },
  {
    juego: "ruleta",
    evento: "tick",
    label: "Tick de la flecha",
    descripcion: "Cada vez que la flecha pasa un segmento (MP3 corto 50-150ms).",
    legacySlot: "tick",
    legacyDependsOnStyle: false,
    synthetic: "click",
  },
  {
    juego: "ruleta",
    evento: "blink",
    label: "Parpadeo del ganador",
    descripcion: "Suena cada vez que el segmento ganador parpadea (3 veces).",
    legacySlot: "blink",
    legacyDependsOnStyle: false,
    synthetic: "blink",
  },
  {
    juego: "ruleta",
    evento: "win",
    label: "Premio ganado",
    descripcion: "Fanfarria que se reproduce con el modal del premio.",
    legacySlot: "win",
    legacyDependsOnStyle: true,
    synthetic: "fanfarria",
  },
  {
    juego: "ruleta",
    evento: "consolation",
    label: "Consuelo / casi pasa",
    descripcion: "Suena cuando toca 'Siga participando' o el casi-pasa.",
    legacySlot: "consolation",
    legacyDependsOnStyle: false,
    synthetic: "consolation",
  },
  // ── Raspadita ──
  {
    juego: "raspadita",
    evento: "scratch",
    label: "Rascado",
    descripcion: "Loopable mientras el cliente raspa con el dedo. Suena tenue.",
    legacySlot: null,
    legacyDependsOnStyle: false,
    synthetic: "scratch",
  },
  {
    juego: "raspadita",
    evento: "reveal",
    label: "Premio revelado",
    descripcion: "Suena al terminar de raspar suficiente y aparecer el premio.",
    legacySlot: null,
    legacyDependsOnStyle: false,
    synthetic: "reveal",
  },
  {
    juego: "raspadita",
    evento: "win",
    label: "Premio ganado",
    descripcion: "Fanfarria que se reproduce con el modal del premio.",
    legacySlot: "win",
    legacyDependsOnStyle: true,
    synthetic: "fanfarria",
  },
  {
    juego: "raspadita",
    evento: "consolation",
    label: "Consuelo",
    descripcion: "Suena cuando el cliente no acierta el match esperado.",
    legacySlot: "consolation",
    legacyDependsOnStyle: false,
    synthetic: "consolation",
  },
  // ── Memoria ──
  {
    juego: "memoria",
    evento: "flip",
    label: "Voltear carta",
    descripcion: "Suena cada vez que el cliente toca una casilla y se revela.",
    legacySlot: null,
    legacyDependsOnStyle: false,
    synthetic: "flip",
  },
  {
    juego: "memoria",
    evento: "match",
    label: "Par correcto",
    descripcion: "Suena cuando el segundo click matchea con el primero.",
    legacySlot: null,
    legacyDependsOnStyle: false,
    synthetic: "match",
  },
  {
    juego: "memoria",
    evento: "mismatch",
    label: "Par fallado",
    descripcion: "Suena cuando el par no coincide y se vuelve a ocultar.",
    legacySlot: null,
    legacyDependsOnStyle: false,
    synthetic: "mismatch",
  },
  {
    juego: "memoria",
    evento: "win",
    label: "Partida ganada",
    descripcion: "Fanfarria con el modal del premio (todos los pares matched).",
    legacySlot: "win",
    legacyDependsOnStyle: true,
    synthetic: "fanfarria",
  },
  {
    juego: "memoria",
    evento: "lose",
    label: "Tiempo agotado",
    descripcion: "Suena con el modal Casi! cuando se acaba el tiempo.",
    legacySlot: null,
    legacyDependsOnStyle: false,
    synthetic: "lose",
  },
];

// Convención: slot key persistido en R2/DB para uploads del refactor.
export function gameSoundSlotKey(
  styleSlug: string,
  juego: GameType,
  evento: string,
): string {
  return `${styleSlug}-${juego}-${evento}`;
}
export type LogoSize = "small" | "medium" | "large";
export type LogoPosition = "left" | "center" | "right";

export type CampoCliente = "nombre" | "telefono" | "cedula" | "email" | "ticket";

export type SucursalSettings = {
  sucursal_id: number;
  registrar_datos_cliente: boolean;
  campos_cliente: CampoCliente[];
  campos_obligatorios: CampoCliente[];
  modo_prueba: boolean;
  ruleta_habilitada: boolean;
  raspadita_habilitada: boolean;
  raspadita_mecanica: RaspaditaMecanica;
  raspadita_grid_dinamica: RaspaditaGridDinamica;
  auto_bloqueo_minutos: number;
  // Física configurable de la ruleta (migration 004).
  ruleta_velocidad_minima: number;
  ruleta_variacion_aleatoria: number;
  ruleta_frenado: number;
  ruleta_umbral_casi_pasa: number;
  // Migration 022: settings del juego memoria por sucursal.
  memoria_habilitada: boolean;
  // Migration 026: tiempo de memorización (al inicio) por nivel.
  // REAL para sub-segundos (ej: difícil 0.8s).
  memoria_seg_mem_facil: number;
  memoria_seg_mem_medio: number;
  memoria_seg_mem_dificil: number;
  memoria_tiempo_facil: number;
  memoria_tiempo_medio: number;
  memoria_tiempo_dificil: number;
  // Migration 026: visuales de la ruleta migrados desde global.
  ruleta_color_borde: string;
  ruleta_color_centro: string;
  ruleta_grosor_divisoria: number;
  ruleta_text_align: "left" | "center" | "right";
  // Migration 029: sonidos por sucursal (antes en global_settings).
  sound_style_slug: SoundStyle;
  sound_enabled: boolean;
};

export type PremioPublic = {
  id: number;
  nombre: string;
  color: string | null;
  peso: number;
  is_consolation: boolean;
  /** Migration 014: si true, el cliente "ganó" un intento extra
   *  (no es premio entregable, solo otra jugada gratis). Mutuamente
   *  exclusivo con is_consolation. */
  is_reintento: boolean;
  sort_order: number;
};

// Response del POST /api/app/play
// play_id es null si:
//  - modo_prueba está activo
//  - O el premio elegido es consolation/reintento (no entregables,
//    no se registran en plays — migration 014)
export type PlayResponse = {
  play_id: number | null;
  prize: PremioPublic;
  position_in_wheel: number; // 0..N-1, índice del segmento
  is_consolation: boolean;
  is_reintento: boolean;
};

// Response del GET /api/app/state
export type AppState = {
  sucursal: {
    id: number;
    nombre: string;
    slug: string;
  };
  settings: SucursalSettings;
  modo_prueba_banner: boolean;
  campana: {
    nombre_display: string;
    imagen_url: string;
  } | null;
  premios: {
    ruleta: PremioPublic[];
    raspadita: PremioPublic[];
  };
  global: {
    logo_url: string | null;
    bg_landscape_url: string | null;
    bg_portrait_url: string | null;
    color_primary: string;
    color_accent: string;
    // Migration 029: sound_style se movió a settings de sucursal.
    // El frontend ahora lo lee de state.settings.sound_style_slug.
    logo_size: LogoSize;
    logo_width_px: number;
    logo_position: LogoPosition;
    campana_max_height_px: number;
    // Migration 034: alto de la campaña DENTRO del juego (separado del
    // alto en la landing). Los juegos lo usan en vez del cap fijo de 80.
    campana_juego_height_px: number;
    // Migration 035: aire arriba de la campaña dentro del juego (posición).
    campana_juego_top_margin_px: number;
    // Migration 037: posición vertical del cuerpo del juego (0..100%).
    juego_vertical_pos_pct: number;
    // Migration 039: tamaño del cuerpo de los juegos sobre el lienzo (cartas de
    // memoria + ancho/alto del panel de raspadita). Compartido vivo↔preview.
    memoria_carta_px: number;
    raspadita_panel_w_px: number;
    raspadita_panel_h_px: number;
    // Migration 040: cap de DPR de los canvas de juego (nitidez vs rendimiento).
    juego_canvas_dpr_max: number;
    // Migration 044: tamaño de la rueda de la ruleta + aire arriba del logo
    // en la pantalla principal.
    ruleta_wheel_size_px: number;
    landing_logo_top_margin_px: number;
    // Migration 045: posición vertical fina del bloque campaña+íconos del landing
    // (0..100%, deslizador). Reemplaza landing_iconos_vertical_align.
    landing_vertical_pos_pct: number;
    // Migration 013/033: íconos custom para los botones de juego.
    // null = el frontend usa el ícono Lucide por defecto.
    icon_ruleta_url: string | null;
    icon_raspadita_url: string | null;
    icon_memoria_url: string | null;
    // Migration 027: tamaño en px de los botones de juego en la
    // landing (los 3 con el mismo valor — decisión del user).
    landing_icon_size_px: number;
    // Migration 026: ruleta_color_borde/centro/grosor_divisoria/
    // text_align migraron a SucursalSettings (cada sucursal personaliza
    // su ruleta sin afectar a las demás).
    // Migration 018: tipografías configurables (3 slots semánticos:
    // títulos, body, texto promocional). El frontend valida que el
    // valor esté en su lista curada de Google Fonts.
    font_heading: string;
    font_body: string;
    font_display: string;
    // Migration 022: 6 imágenes del tablero del juego memoria.
    // null = slot vacío. El juego solo se ofrece si las 6 están.
    memoria_imagen_1_url: string | null;
    memoria_imagen_2_url: string | null;
    memoria_imagen_3_url: string | null;
    memoria_imagen_4_url: string | null;
    memoria_imagen_5_url: string | null;
    memoria_imagen_6_url: string | null;
    // Migration 031: layout configurable del landing.
    landing_campana_to_iconos_gap_px: number;
    landing_iconos_vertical_align: "top" | "center" | "bottom";
    landing_iconos_horizontal_align: "left" | "center" | "right";
    landing_campana_top_margin_px: number;
  };
  // Migration 017: map { slot_key: url } de sonidos cargados desde
  // admin. Si un slot no está acá, el frontend usa el sintético del
  // lib/audio.ts. Slots conocidos: tick, spin-start, blink,
  // win-classic, win-casino, win-festivo, win-mundial, consolation,
  // retry. Se pueden agregar más sin tocar el schema de la tabla.
  sounds: Record<string, string>;
};

// Request body para POST /api/app/play
export type PlayRequest = {
  game_type: GameType;
  customer?: {
    nombre?: string;
    telefono?: string;
    cedula?: string;
    email?: string;
    ticket?: string;
  };
};

// ──────────────────────────────────────────────
// Juego MEMORIA — máquina de estados de fin de partida
// ──────────────────────────────────────────────
// La memoria es el ÚNICO juego con timer concurrente + detección de
// victoria — ambos pueden disparar al mismo tiempo y pisarse, dejando
// al cliente con DOS modales (ganaste + perdiste) o uno incorrecto.
// Este FSM define la lógica de transición como función PURA, importable
// tanto desde el componente React (con setFase functional updater) como
// desde tests vitest sin necesidad de renderizar nada.
//
// Invariante crítica: una vez que la partida termina ('ganaste' o
// 'perdiste'), NUNCA puede transicionar a la otra. El primer evento
// que llega "gana" — los siguientes son no-ops.
//
// Estados:
//   - 'jugando': partida en progreso, esperando matcheos o fin de timer.
//   - 'ganaste': cliente matcheó todos los pares dentro del tiempo.
//   - 'perdiste': el tiempo se acabó sin matcheo completo.
//
// Eventos:
//   - 'all-matched': useEffect detectó grid.every(c => c.estado ==
//     'matcheada'). Dispara cuando cae el último par.
//   - 'timer-zero': el countdown llegó a 0.
//
// El componente usa esta función como:
//   setFase((current) => nextMemoriaFase(current, "timer-zero"));
//   setFase((current) => nextMemoriaFase(current, "all-matched"));
//
// setFase functional updater garantiza que la transición SIEMPRE ve el
// current state, no el closure stale del effect/handler.
export type MemoriaFase = "jugando" | "ganaste" | "perdiste";
export type MemoriaEvent = "all-matched" | "timer-zero";

export function nextMemoriaFase(
  current: MemoriaFase,
  event: MemoriaEvent,
): MemoriaFase {
  // Si ya terminó la partida, NUNCA pisar — preserva el primer
  // resultado decidido (regla anti-race que protege al cliente y al
  // admin: solo aparece UN modal).
  if (current !== "jugando") return current;
  // Switch exhaustivo + assertNever: si en el futuro se agrega un
  // nuevo evento a MemoriaEvent, TypeScript rompe el build acá
  // hasta que se agregue su case explícito. Con el ternario anterior
  // el agregado caía silencioso en 'perdiste' (regresión latente).
  switch (event) {
    case "all-matched":
      return "ganaste";
    case "timer-zero":
      return "perdiste";
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}

// Response GET /api/admin/me
export type MeResponse = {
  email: string | null;
  role: Role | null;
  sucursal_id: number | null;
  // Nombre de la sucursal asignada (NULL para super_admin que no
  // tiene sucursal fija). Joined desde la tabla sucursales en /admin/me.
  sucursal_nombre: string | null;
  // unix timestamp de cuándo se creó el user_role. Para el modal de
  // perfil ("Miembro desde X"). null si no se pudo determinar.
  created_at: number | null;
};
